// One worklist for name mapping: every row a company, ranked by what is at
// stake. Replaces the four calls the alias screen used to make — the groups,
// the suggestions and the 789-item picker — with
// one paginated read (0.13s against 0.68s on the live engagement).
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { CATEGORIES, categoryMeta } from '../format'
import { api, postJson } from './client'
import type { NameMappingPage, NameMappingRow, NameMappingZones } from './types'
import vocab from './vocabulary.json'

export interface NameMappingParams {
  state: string
  search: string
  sort: 'value' | 'name'
  page: number
  size: number
}

export function useNameMapping(runId: string | null, p: NameMappingParams) {
  const qs = new URLSearchParams({
    state: p.state,
    sort: p.sort,
    page: String(p.page),
    size: String(p.size),
  })
  if (p.search) qs.set('search', p.search)
  return useQuery({
    queryKey: ['name-mapping', runId, qs.toString()],
    queryFn: () => api<NameMappingPage>(`/runs/${runId}/name-mapping?${qs}`),
    enabled: runId != null,
    placeholderData: keepPreviousData,
  })
}

/** Accept the engine's pairing for a row.
 *
 *  Posts the two NAMES, not a pair of regexes: `create_group_from_run` plans
 *  them against the run, which is what stops an alias stranding a name the
 *  party already owns on the other side (`alias_plan`). It also records the
 *  reach it just showed, so a mapping made here arrives signed.
 */
export function useAcceptPairing(runId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { names_26as: string[]; names_books: string[]; label?: string }) =>
      postJson(`/runs/${runId}/alias-groups`, p),
    onSuccess: () => invalidate(qc),
  })
}

/** Every query the worklist and its neighbours read. One place, because a
 *  mapping created here changes the row list, the alias screen and the
 *  suggestion cache's coverage at the same time.
 *
 *  `parties` and `grid` joined the list when matching moved inside the category
 *  sections: an alias made in "In 26AS only" changes the mirror section's list
 *  and both headings' counts. `name-mapping` was already here but
 *  `useCreateAliasFromRun` was not calling this at all, so the header cards
 *  went stale after every alias built from the picker.
 */
export function invalidate(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ['name-mapping', 'alias-groups', 'alias-suggestions',
    'alias-candidates', 'parties', 'grid']) {
    void qc.invalidateQueries({ queryKey: [k] })
  }
}

/** The two names a `proposed` row would join, in the order the endpoint needs. */
export function pairingOf(r: NameMappingRow): { names_26as: string[]; names_books: string[] } {
  if (!r.proposal) return { names_26as: [], names_books: [] }
  return r.side === '26as'
    ? { names_26as: [r.title], names_books: [r.proposal.name] }
    : { names_26as: [r.proposal.name], names_books: [r.title] }
}

/** Confirm an `engine_join` row: the party's FULL membership on both sides,
 *  exactly as the engine welded it. A subset would strand the other spellings
 *  — the defect `alias_plan` exists to prevent — and the server signs the
 *  reach off precisely this list. */
export function confirmJoin(r: NameMappingRow): { names_26as: string[]; names_books: string[] } {
  return { names_26as: r.names_26as, names_books: r.names_books }
}

/** The Excel's Name Match Basis column, as a badge text.
 *
 *  Null for the one-sided pseudo-bases — being unmatched is a state the row
 *  already shows, not a join to describe. `exact` carries no score: exact IS
 *  the score, and printing 1.00 beside it invites the reader to wonder what a
 *  0.99 exact would have been. A manual alias shows the confidence a person
 *  recorded, never a number nobody computed.
 */
export function basisLabel(basis: string | null, score: number | null): string | null {
  if (!basis || basis === '26AS only' || basis === 'Ledger only') return null
  const manual = basis.match(/^manual alias \((.+)\)$/)
  if (manual) return `your alias · ${manual[1]}`
  if (basis === 'exact') return 'same name'
  // item 8: the engine's vocabulary in the reader's words. A prefix weld
  // joins on startswith, not a ratio — it earns a "check"; fuzzy is a
  // similarity judgement and says so.
  if (basis === 'prefix') {
    return score != null
      ? `joined on prefix ${score.toFixed(2)} — check`
      : 'joined on prefix — check'
  }
  if (basis === 'fuzzy') {
    return score != null ? `similar name ${score.toFixed(2)}` : 'similar name'
  }
  return score != null ? `${basis} ${score.toFixed(2)}` : basis
}

/** Partition the rows into the page's three zones.
 *
 *  One function, unit-tested, so the page renders from a partition that
 *  provably covers every row exactly once — the mixed-state list this replaces
 *  failed precisely by asking one table to be all three zones at once.
 *
 *  `match` is deliberately not rendered as a list: those rows are the manual
 *  columns' feedstock, and a proposal below the suggested bar rides along
 *  there as a ranked candidate — the machine's opinion beside your judgement,
 *  never the headline.
 */
export function zonesOf(rows: NameMappingRow[]): NameMappingZones {
  const out: NameMappingZones = {
    suggested: [],
    match: [],
    withheld: [],
    decided: { mapped: [], engineJoined: [], claimed: [], retired: [] },
  }
  for (const r of rows) {
    if (r.proposal?.suggested) out.suggested.push(r)
    else if (r.state === 'needs_decision' || r.state === 'proposed') out.match.push(r)
    else if (r.state === 'withheld') out.withheld.push(r)
    else if (r.state === 'engine_joined') out.decided.engineJoined.push(r)
    else if (r.state === 'claimed') out.decided.claimed.push(r)
    else if (r.state === 'retired') out.decided.retired.push(r)
    else out.decided.mapped.push(r)      // in_force + awaiting_approval
  }
  return out
}

export interface CategorySection {
  /** the engine's own string, and the value the parties endpoint filters on */
  category: string
  short: string
  color: string
  count: number
}

/** The Excel's Party Reconciliation picture, as sections to open.
 *
 *  This used to be one dimmed line of text — "43 agreed · 111 with difference
 *  · …" — under the header. It is the sheet a CA actually reads, and it was the
 *  only thing on the page with no way to see WHICH parties it counted.
 *
 *  Ordered by `CATEGORIES`, the engine's own emission order, not by count:
 *  agreed first and problems after is how the sheet reads, and an order that
 *  shuffles when the data changes cannot be learned. A category this build has
 *  never heard of is kept and appended rather than dropped — the engine is
 *  free to grow one, and silently swallowing it would hide parties.
 */
export function categorySections(
  categories: Record<string, number> | undefined,
): CategorySection[] {
  if (!categories) return []
  const known = CATEGORIES.filter((c) => c in categories)
  const rest = Object.keys(categories).filter((c) => !CATEGORIES.includes(c))
  return [...known, ...rest]
    .filter((c) => (categories[c] ?? 0) > 0)
    .map((c) => ({ category: c, count: categories[c], ...categoryMeta(c) }))
}

/** Which side of the run a column shows. Structurally the same union as
 *  `AliasPicker`'s `Side`, declared here so `api/` never imports from
 *  `components/`. */
export type MatchSide = '26as' | 'books'

export interface WorkbenchState {
  /** the section's own list — the names it counts */
  primary: MatchSide
  /** the other side, where the candidates are */
  secondary: MatchSide
  /** what the secondary column ranks against. PINNED to the primary. */
  against: { name: string; side: MatchSide } | undefined
  selection: { names_26as: string[]; names_books: string[] }
  ready: boolean
}

/** Only the two one-sided categories carry a workbench. A matched party has no
 *  naming question left, and NIL-TDS is 26AS-only but worth ₹0 — there is no
 *  shortfall for an alias to close, so offering one would be busywork. */
const WORKBENCH: Record<string, { primary: MatchSide; secondary: MatchSide }> = {
  [vocab.categories.only_26as]: { primary: '26as', secondary: 'books' },
  [vocab.categories.only_books]: { primary: 'books', secondary: '26as' },
}

/** The zone tab to open on (density pass A2): the first one-sided zone with
 *  names to decide — the zones the workbench lives in — else the first
 *  populated zone. "The action is almost hidden" was the finding, and opening
 *  on Agreed, an archive, was half of why. */
export function defaultZone(
  categories: Record<string, number> | undefined,
): string | null {
  const sections = categorySections(categories)
  if (!sections.length) return null
  const actionable = sections.find((c) => c.category in WORKBENCH && c.count > 0)
  return (actionable ?? sections[0]).category
}

/** The suggestions that belong to one category's section.
 *
 *  `SuggestedPairs` rendered above the categories: the reader met the
 *  machine's recommendations before the list they came from, then met the same
 *  names again inside it. The section already counts those names — "In 26AS
 *  only (76)" — and already holds the two columns that act on them, so a
 *  recommendation about one of the 76 belongs there and nowhere else.
 *
 *  Keyed off `WORKBENCH`, the same map `workbenchState` reads, so the sections
 *  that can hold a suggestion are exactly the sections that can act on one. A
 *  matched party has no naming question left, and NIL-TDS is 26AS-only but
 *  worth Rs 0 — neither gets a workbench, so neither gets a suggestion.
 *
 *  The row's own `side` places it: a proposal for a 26AS-only name is that
 *  name's decision, listed where that name is counted, whichever side the
 *  counterpart sits on.
 *
 *  THE BAR IS UNTOUCHED, on measurement rather than by omission. Run 41: 24
 *  proposals — 17 weak, 5 likely, 2 strong — and 0 pass. Both strong pairs
 *  close nothing and are different companies: NATIONAL INSTITUTE OF BANK
 *  MANAGEMENT against NATIONAL INSTITUTE OF VIROLOGY, which is the case
 *  `suggested()` itself cites, and SYNGENTA INDIA against SYNGENTA SERVICES.
 *  Four of the five `likely` ones WOULD close money — Rs 64,412 between
 *  MAHINDRA & MAHINDRA and TECH MAHINDRA, HEALTH METER and HEALTH INDIA, THE
 *  BISHOPS and MODERN EDUCATION SOCIETY, BHARAT and MAHARASHTRA ELECTRONICS.
 *  Loosening the band would put all four forward as the machine's own
 *  recommendation, so an empty list here is the gate working, not failing.
 */
export function suggestionsFor(
  rows: NameMappingRow[],
  category: string,
): NameMappingRow[] {
  const sides = WORKBENCH[category]
  if (!sides) return []
  return rows.filter((r) => r.side === sides.primary)
}

/** The strong pairs a gate kept OUT of Suggested, for one section.
 *
 *  Measured on run 45: all 11 strong-band pairs died at the crossed-guard and
 *  the Suggested zone rendered empty — the guard right eleven times (INFOSYS
 *  both directions, TATA 1MG↔TATA TECHNOLOGIES, and pairs whose counterpart
 *  the user had already mapped), and the screen saying nothing about any of
 *  it. These render at the top of the section WITH their reasons: strong on
 *  top, no false certainty, and the gate's work finally visible.
 */
export function strongCandidates(
  rows: NameMappingRow[],
  category: string,
): NameMappingRow[] {
  const sides = WORKBENCH[category]
  if (!sides) return []
  return rows.filter((r) =>
    r.side === sides.primary && r.kind === 'name'
    && r.proposal != null && r.proposal.band === 'strong'
    && !r.proposal.suggested && r.proposal.gate != null)
}

/** The gate's reason, in the reader's words. */
export function gateText(gate: 'crossed' | 'closes_zero' | null | undefined): string {
  if (gate === 'crossed') {
    return 'one of these names already belongs to a company with its own '
      + 'other side — the run knows them as different companies'
  }
  if (gate === 'closes_zero') {
    return 'their parties already tie — mapping them would close ₹0'
  }
  return ''
}

/** Turn "which section, what is picked" into everything the workbench needs.
 *
 *  Pure, because the interesting rules are all decisions rather than rendering:
 *  which side leads, what the request body looks like, and — the one this
 *  exists for — that the ranking stays pinned to the primary name however many
 *  counterparts get ticked.
 *
 *  `AliasPicker` computes its own `against` as "exactly one name on one side
 *  and none on the other", so ticking a second book name drops the ranking and
 *  the column silently re-sorts by rupees underneath the reader's cursor. Here
 *  the question never changes: "which of these is the company selected on the
 *  left". Ticking a second spelling of the answer is not a new question.
 */
export function workbenchState(
  category: string,
  primaryName: string | null,
  secondaryNames: string[],
): WorkbenchState | null {
  const sides = WORKBENCH[category]
  if (!sides) return null
  const { primary, secondary } = sides
  const own = primaryName ? [primaryName] : []
  return {
    primary,
    secondary,
    against: primaryName ? { name: primaryName, side: primary } : undefined,
    selection: primary === '26as'
      ? { names_26as: own, names_books: secondaryNames }
      : { names_26as: secondaryNames, names_books: own },
    // an alias needs a name on each side; the server refuses otherwise
    ready: own.length > 0 && secondaryNames.length > 0,
  }
}

export type PartySortMode = 'name' | 'difference' | 'confidence'

/** Order a category panel's parties.
 *
 *  `difference` is the panel's own order — biggest gap first — and answers
 *  "what is worth looking at".
 *
 *  `confidence` answers a different question that used to have its own section:
 *  which of the engine's own judgements is most likely to be wrong. The engine
 *  welds names into one party by prefix or fuzzy match, and on the live
 *  engagement BAYER ZYDUS is welded at prefix 0.419 — a weld that joins on
 *  `startswith`, not on a ratio. Inside a 110-row table sorted by difference it
 *  is invisible, which is why the standalone list existed; this ordering is
 *  what replaces it.
 *
 *  A party with no score sits below every scored one. `exact` carries no score
 *  because exact IS the score, and it is not what this ordering is looking for.
 */
export function partySort<T extends { party: string; diff: string }>(
  rows: T[],
  mode: PartySortMode,
  welded: Map<string, number | null>,
): T[] {
  const gap = (r: T) => Math.abs(Number(r.diff) || 0)
  const out = [...rows]
  if (mode === 'name') {
    // A-Z by the party's 26AS-side label, with one amendment (nine-point
    // round, 1b): parties the engine joined that still await a human —
    // Keep together / Split them — sort FIRST, so the default view opens
    // on the decisions instead of hiding them mid-alphabet. `welded` is
    // exactly that set (engine-joined, no approved alias yet).
    const tier = (r: T) => (welded.has(r.party) ? 0 : 1)
    return out.sort((a, b) =>
      tier(a) - tier(b)
      || a.party.localeCompare(b.party, 'en', { sensitivity: 'base' }))
  }
  if (mode !== 'confidence') {
    return out.sort((a, b) => gap(b) - gap(a))
  }
  const rank = (r: T) => {
    if (!welded.has(r.party)) return 2          // not welded at all
    return welded.get(r.party) == null ? 1 : 0  // welded, but unscored
  }
  return out.sort((a, b) =>
    rank(a) - rank(b)
    || (welded.get(a.party) ?? 0) - (welded.get(b.party) ?? 0)
    || gap(b) - gap(a))
}

// from the GENERATED vocabulary — the backend taxonomy is the one source;
// the gate's contract step fails on drift (contract separation, Stage 2)
const CAT_AGREED = vocab.categories.agreed
const CAT_DIFF = vocab.categories.with_difference
const CAT_ONLY26 = vocab.categories.only_26as
const CAT_ONLYBK = vocab.categories.only_books
const CAT_NIL = vocab.categories.nil

export interface HeaderLine {
  label: string
  total: number
  unit: string
  /** the same line restated in the other unit — "307 names in 239 parties".
   *  Null on a line that has only one honest unit. */
  derived: { n: number; unit: string } | null
  parts: { n: number; label: string }[]
}

/** The header, as lines that each add up.
 *
 *  It was six tiles in a row — 305 / 216 / 153 / 128 / two rupee figures — in
 *  THREE units with none of them labelled: parties, worklist rows, rupees. A
 *  reader naturally tries 153 + 128 against 305, and it can never reconcile,
 *  because 153 counts parties and 128 counts rows.
 *
 *  So the party figures become lines that close against themselves:
 *
 *      26AS   305 = 153 matched + 146 unmatched + 6 NIL TDS
 *      Books  216 = 153 matched +  63 unmatched
 *             153 = 43 agreed   + 110 with difference
 *
 *  and 146 / 63 / 43 / 110 are visibly the section headings underneath.
 *
 *  Each line carries a residual `other` when the categories this build knows do
 *  not account for the whole total. The engine may grow a category, and a line
 *  that silently stops adding up is worse than one that admits it cannot name
 *  the difference.
 *
 *  U1: THE SIDE LINES ARE IN NAMES, AND DERIVE PARTIES
 *
 *  P2 made every line close but left the unit unstated, and the unit was
 *  parties — under headings a reader takes for names. The user counted the
 *  deductor list, got 306, and was shown 239. Both true; nothing bridged them.
 *
 *      26AS   307 names in 239 parties = 225 matched + 76 unmatched + 6 NIL
 *      Books  234 names in 215 parties = 176 matched + 58 unmatched
 *             157 matched PARTIES      =  43 agreed  + 114 with difference
 *
 *  Names are what the lists below show and what a person ticks, so the side
 *  lines state names; parties are the derived figure, named as such. The third
 *  line cannot be restated — a party is agreed or it is not, so a category is a
 *  property of a party — and it therefore carries its own unit rather than
 *  inheriting the one above it.
 *
 *  Every part comes from `summary.names`, never from the party categories. The
 *  two agree on the one-sided parts today because no one-sided party is
 *  multi-name (U0), which is a fact about this engagement's data and not about
 *  the model: one deductor spelled two ways and absent from books breaks it.
 */
export function headerLines(summary: NameMappingPage['summary']): HeaderLine[] {
  const c = summary.categories ?? {}
  const p = summary.parties
  const n = summary.names
  const n26 = n.by_category_26as ?? {}
  const nbk = n.by_category_books ?? {}
  const line = (
    label: string, total: number, unit: string,
    derived: { n: number; unit: string } | null,
    parts: { n: number; label: string }[],
  ): HeaderLine => {
    const known = parts.reduce((a, x) => a + x.n, 0)
    const rest = total - known
    return {
      label,
      total,
      unit,
      derived,
      parts: rest === 0 ? parts : [...parts, { n: rest, label: 'other' }],
    }
  }
  return [
    line('26AS', n.in_26as, 'names', { n: p.in_26as, unit: 'parties' }, [
      { n: (n26[CAT_AGREED] ?? 0) + (n26[CAT_DIFF] ?? 0), label: 'matched' },
      { n: n26[CAT_ONLY26] ?? 0, label: 'unmatched' },
      { n: n26[CAT_NIL] ?? 0, label: 'NIL TDS' },
    ].filter((x) => x.n > 0)),
    line('Books', n.in_books, 'names', { n: p.in_books, unit: 'parties' }, [
      { n: (nbk[CAT_AGREED] ?? 0) + (nbk[CAT_DIFF] ?? 0), label: 'matched' },
      { n: nbk[CAT_ONLYBK] ?? 0, label: 'unmatched' },
    ].filter((x) => x.n > 0)),
    line('', p.matched, 'parties', null, [
      { n: c[CAT_AGREED] ?? 0, label: 'agreed' },
      { n: c[CAT_DIFF] ?? 0, label: 'with difference' },
    ].filter((x) => x.n > 0)),
  ]
}

/** How many one-sided NAMES still need a decision.
 *
 *  Deliberately not `summary.open.n`. That counts worklist ROWS, and a row that
 *  pairs a 26AS name with its books counterpart covers two names — nine such
 *  rows on run 32, which is why the tile read 128 while the answer is 137. The
 *  row count is a property of the list; this is a property of the run.
 *
 *  The identity holds because one-sided names are exactly the two one-sided
 *  categories, which a backend test pins.
 *
 *  U1: counted off `summary.names`, not the party categories. It read the party
 *  ones and was right by coincidence — 76 + 58 = 134 in either unit, because no
 *  one-sided party is multi-name on this data. The tile said names and counted
 *  parties, which is the P2 defect surviving inside P2's own fix.
 */
export function namesToDecide(summary: NameMappingPage['summary']): number {
  const p = namesToDecideParts(summary)
  return p.only_26as + p.only_books - p.claimed
}

/** What the figure above is made of.
 *
 *  Added after the visual pass: on a header whose whole point is that every
 *  number says what it counts and adds up, this was the one card with nothing
 *  underneath it. The subtraction is the part worth showing — a name an
 *  approved group already covers resolves on the NEXT run, so it is not
 *  outstanding work, and silently deducting it is how 137 stopped looking like
 *  146 + 63.
 */
export function namesToDecideParts(summary: NameMappingPage['summary']): {
  only_26as: number
  only_books: number
  claimed: number
} {
  const n26 = summary.names.by_category_26as ?? {}
  const nbk = summary.names.by_category_books ?? {}
  return {
    only_26as: n26[CAT_ONLY26] ?? 0,
    only_books: nbk[CAT_ONLYBK] ?? 0,
    claimed: summary.claimed.n,
  }
}

/** Declare that one name is its own company, so nothing welds it to a
 *  neighbour.
 *
 *  The engine's pass 0 removes an alias group's names from the auto-match pools
 *  before the prefix and fuzzy passes run. A group matching ONE side and
 *  flagged `do_not_join` therefore holds that name out of reach of both — which
 *  is the only way to undo a wrong weld. Measured on run 41: BAJAJ FINSERV
 *  LIMITED (2 rows, Rs 140) welded into BAJAJ FINSERV HEALTH LIMITED by a
 *  prefix hit, at score 1.000. Two different legal entities.
 *
 *  The other side gets `(?!)` — a regex that matches nothing, ever — because
 *  the group is deliberately one-sided and the flag is what says so. An
 *  unflagged one-sided group is still warned as unused, which is how an author
 *  learns their pattern missed a side.
 */
export function standAlone(name: string, side: '26as' | 'books'): {
  label: string
  regex_26as: string
  regex_ledger: string
  confidence: string
  do_not_join: true
} {
  // anchored and fully escaped: this must match THIS name and no other. Real
  // company names carry '.', '(', ')', '&' and '+', and an unescaped dot would
  // let the pattern claim a different company whose name differs by one char.
  const exact = `^${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`
  const never = '(?!)'
  return {
    label: name,
    regex_26as: side === '26as' ? exact : never,
    regex_ledger: side === 'books' ? exact : never,
    confidence: 'High',
    do_not_join: true,
  }
}
